import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/shared/FormField';
import { toast } from '@/lib/swal';
import {
  getVersions,
  publishMaster,
  type EmailTemplateMaster,
  type PublishReleaseNotesInput,
  type VariableDefinition,
} from '../api/platform-email-template.api';
import { diffVariables, extractVariables, validateTemplate } from '../lib/template-validation';

interface Props {
  open: boolean;
  template: EmailTemplateMaster;
  variables: VariableDefinition[];
  onClose: () => void;
  onPublished: () => void;
}

/** Comma/newline separated free text → trimmed list. */
function toList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Publish dialog (COMMUNICATION_DESIGN.md §8.1 / §8.3).
 *
 * Release notes are mandatory (backend-validated too) and the variable delta is
 * computed automatically against the last published version. Validation runs
 * first and blocks publishing while any rule fails.
 */
export function PublishDialog({ open, template, variables, onClose, onPublished }: Props) {
  const [summary, setSummary] = useState('');
  const [featuresAdded, setFeaturesAdded] = useState('');
  const [featuresRemoved, setFeaturesRemoved] = useState('');
  const [breakingChanges, setBreakingChanges] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previousVars, setPreviousVars] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) {
      setSummary('');
      setFeaturesAdded('');
      setFeaturesRemoved('');
      setBreakingChanges('');
      setError(null);
      setPreviousVars(null);
      return;
    }
    // Variable delta is computed against the most recent published version.
    void (async () => {
      try {
        const versions = await getVersions(template.id);
        if (versions.length === 0) {
          setPreviousVars([]);
          return;
        }
        const latest = versions.reduce((n, v) => (v.createdAt > n.createdAt ? v : n));
        setPreviousVars(extractVariables(latest.subject, latest.htmlBody, latest.plainTextBody));
      } catch {
        setPreviousVars([]);
      }
    })();
  }, [open, template.id]);

  const validation = useMemo(
    () =>
      validateTemplate({
        subject: template.subject,
        htmlBody: template.htmlBody,
        plainTextBody: template.plainTextBody,
        knownVariableKeys: variables.map((v) => v.key),
      }),
    [template, variables],
  );

  const currentVars = useMemo(
    () => extractVariables(template.subject, template.htmlBody, template.plainTextBody),
    [template],
  );

  const varDelta = useMemo(
    () => diffVariables(previousVars ?? [], currentVars),
    [previousVars, currentVars],
  );

  const canPublish = validation.valid && summary.trim().length > 0 && !publishing;

  const handlePublish = async () => {
    setError(null);
    setPublishing(true);
    try {
      // The variable delta is shown above for the publisher's benefit but is
      // deliberately NOT submitted: the backend recomputes it in
      // publishVersion() and `ReleaseNotesDto` does not accept those fields, so
      // sending them fails the whole request under forbidNonWhitelisted.
      const notes: PublishReleaseNotesInput = {
        summary: summary.trim(),
        featuresAdded: toList(featuresAdded),
        featuresRemoved: toList(featuresRemoved),
        breakingChanges: breakingChanges.trim(),
      };
      const result = await publishMaster(template.id, notes);
      toast.success(`Published v${result.currentVersion}`);
      onPublished();
    } catch (e) {
      // Inline, not a toast — a failed mutation must stay visible in the dialog (§8.4).
      setError(e instanceof Error ? e.message : 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Publish ${template.name}`} size="lg">
      <div className="space-y-4">
        {/* Validation gate */}
        {validation.valid ? (
          <div className="flex items-start gap-2 rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2">
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="mt-0.5 h-3.5 w-3.5 text-green-600 dark:text-green-400"
            />
            <p className="text-xs text-foreground">
              Validation passed — HTML, variables and syntax are clean.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mt-0.5 h-3.5 w-3.5 text-destructive"
              />
              <div>
                <p className="text-xs font-medium text-foreground">
                  Publishing is blocked — {validation.issues.length} validation{' '}
                  {validation.issues.length === 1 ? 'issue' : 'issues'}
                </p>
                <ul className="mt-1.5 space-y-1">
                  {validation.issues.map((issue, i) => (
                    <li key={`${issue.rule}-${i}`} className="text-[11px] text-muted-foreground">
                      <span className="font-medium text-foreground">{issue.rule}:</span>{' '}
                      {issue.message}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Computed variable delta */}
        <div className="rounded-lg border border-border bg-muted/20 px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Variable changes (computed)
          </p>
          {previousVars === null ? (
            <p className="mt-1 text-xs text-muted-foreground">Computing…</p>
          ) : varDelta.added.length === 0 && varDelta.removed.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              No variable changes since the last published version.
            </p>
          ) : (
            <div className="mt-1 space-y-1">
              {varDelta.added.length > 0 && (
                <p className="text-xs">
                  <span className="text-green-600 dark:text-green-400">Added:</span>{' '}
                  <span className="font-mono text-[11px] text-foreground">
                    {varDelta.added.join(', ')}
                  </span>
                </p>
              )}
              {varDelta.removed.length > 0 && (
                <p className="text-xs">
                  <span className="text-destructive">Removed:</span>{' '}
                  <span className="font-mono text-[11px] text-foreground">
                    {varDelta.removed.join(', ')}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Release notes — mandatory */}
        <FormField label="Summary" htmlFor="publish-summary" required>
          <Input
            id="publish-summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="What changed in this version?"
            disabled={publishing}
          />
        </FormField>

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Features added" htmlFor="publish-features-added">
            <textarea
              id="publish-features-added"
              value={featuresAdded}
              onChange={(e) => setFeaturesAdded(e.target.value)}
              rows={3}
              disabled={publishing}
              placeholder="One per line"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
          </FormField>
          <FormField label="Features removed" htmlFor="publish-features-removed">
            <textarea
              id="publish-features-removed"
              value={featuresRemoved}
              onChange={(e) => setFeaturesRemoved(e.target.value)}
              rows={3}
              disabled={publishing}
              placeholder="One per line"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
          </FormField>
        </div>

        <FormField label="Breaking changes" htmlFor="publish-breaking">
          <textarea
            id="publish-breaking"
            value={breakingChanges}
            onChange={(e) => setBreakingChanges(e.target.value)}
            rows={2}
            disabled={publishing}
            placeholder="Leave empty if none"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          />
        </FormField>

        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-3">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={publishing}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void handlePublish()}
            disabled={!canPublish}
            title={
              !validation.valid
                ? 'Fix the validation issues first'
                : !summary.trim()
                  ? 'A summary is required'
                  : undefined
            }
          >
            {publishing && (
              <FontAwesomeIcon icon={faSpinner} className="mr-1.5 h-3 w-3 animate-spin" />
            )}
            Publish v{template.currentVersion + 1}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
